<!--

  @copyright Copyright (c) Open-Xchange GmbH, Germany <info@open-xchange.com>
  @license AGPL-3.0

  This code is free software: you can redistribute it and/or modify
  it under the terms of the GNU Affero General Public License as published by
  the Free Software Foundation, either version 3 of the License, or
  (at your option) any later version.

  This program is distributed in the hope that it will be useful,
  but WITHOUT ANY WARRANTY; without even the implied warranty of
  MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE. See the
  GNU Affero General Public License for more details.

  You should have received a copy of the GNU Affero General Public License
  along with OX App Suite. If not, see <https://www.gnu.org/licenses/agpl-3.0.txt>.

  Any use of the work other than as authorized under this license or copyright law is prohibited.

-->

<template>
  <svg
    v-if="html"
    v-bind="attributes"
    v-html="html"
    :width="width"
    :height="height"
    aria-hidden="true"
    class="illustration"
    fill="currentColor"
    role="presentation"
  >
  </svg>
</template>

<script setup>
import { ref, computed } from 'vue'
import { settings as coreSettings } from '@/io.ox/core/settings'
import { useFetch } from '@vueuse/core'

const props = defineProps({
  src: {
    type: String,
    required: true
  },
  width: {
    type: [Number, String],
    validator: value => typeof value === 'number' || value === 'auto',
    required: false
  },
  height: {
    type: [Number, String],
    validator: value => typeof value === 'number' || value === 'auto',
    required: false
  }
})

const attributes = ref({})
const customIcons = coreSettings.get('theming/customIcons', {})
const path = computed(() => {
  return customIcons[props.iconName] || (props.src.startsWith('bi/')
    ? `./themes/default/icons/${props.src}`
    : `./themes/default/${props.src}`)
})

const { data } = useFetch(path, { refetch: true }).get().text()
const parser = new DOMParser()
const html = computed(() => {
  if (!data.value) return ''

  const illustration = parser
    .parseFromString(data.value, 'image/svg+xml')
    .getElementsByTagName('svg')[0]

  for (const { name, value } of (illustration.attributes || [])) {
    // eslint-disable-next-line vue/no-side-effects-in-computed-properties
    attributes.value[name] = value
  }

  return illustration.innerHTML
})

</script>

<style scoped>
.illustration {
  opacity: 1;
}
</style>
